import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "../assets/logo.png";
import ThemeToggle from "./ThemeToggle";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n";

const navLinks = [
  { key: "services", path: "/services" },
  { key: "about", path: "/about" },
  { key: "guides", path: "/guide" },
  { key: "contact", path: "/contact" },
];

const Header = () => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5">
      <div
        className={`mx-auto flex h-16 w-full max-w-6xl items-center justify-between rounded-full border px-3 ps-5 transition-all duration-300 2xl:max-w-7xl ${
          scrolled || isMenuOpen
            ? "border-line bg-paper/80 shadow-lg shadow-black/5 backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <Link to="/" className="group flex items-center gap-2.5" aria-label="Optivra Tech">
          <img
            src={logo}
            alt=""
            className="h-8 w-8 object-contain transition-transform duration-700 group-hover:rotate-[360deg]"
          />
          <span className="text-[1.0625rem] font-semibold tracking-tight text-ink">Optivra Tech</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-[0.9375rem] transition-colors ${
                  isActive ? "bg-ink/[0.07] text-ink" : "text-ink-2 hover:bg-ink/[0.04] hover:text-ink"
                }`
              }
            >
              {t(`common.nav.${link.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher />
          <ThemeToggle />
          <Link to="/checkout" className="btn btn-dark btn-sm ms-1">
            {t("common.cta")}
          </Link>
        </div>

        <div className="flex items-center gap-1.5 lg:hidden">
          <LanguageSwitcher />
          <ThemeToggle />
          <button
            className="btn-icon transition-colors active:scale-90"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? t("common.menuClose") : t("common.menuOpen")}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <nav
          className="pop-in mx-auto mt-2 max-w-6xl rounded-3xl border border-line bg-paper/95 p-3 shadow-xl backdrop-blur-xl lg:hidden"
          aria-label="Mobile"
        >
          {navLinks.map((link, i) => (
            <NavLink
              key={link.path}
              to={link.path}
              style={{ animationDelay: `${i * 50}ms` }}
              className={({ isActive }) =>
                `pop-in block rounded-2xl px-4 py-3.5 text-lg transition-colors ${
                  isActive ? "bg-brand-soft text-brand" : "text-ink hover:bg-ink/5"
                }`
              }
            >
              {t(`common.nav.${link.key}`)}
            </NavLink>
          ))}
          <Link to="/checkout" className="btn btn-primary mt-2 w-full">
            {t("common.cta")}
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
